import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  PlaceOutlined,
  KingBedOutlined,
  StairsOutlined,
  ChairOutlined,
  KitchenOutlined,
  PhotoLibraryOutlined,
  ImageNotSupportedOutlined,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { loadPhotos } from "../api/publicApi";
import PhotoImage from "../../components/PhotoImage";
import {
  districtLabel,
  formatPrice,
  listingTitle,
  relativeDate,
  roomsLabel,
  floorLabel,
} from "../utils/format";
import { tokens, radius, shadow } from "../../theme/theme";
import { CHRISTMAS, HALLOWEEN } from "../halloween/season";
import CardSpooks, { CARD_SPOOK_HOVER } from "../halloween/CardSpooks";
import CardFrost, { CARD_FROST_HOVER } from "../christmas/CardFrost";

function Feature({ icon, children }) {
  if (!children) return null;
  return (
    <Stack direction="row" spacing={0.6} alignItems="center" sx={{ color: tokens.ink500 }}>
      {React.cloneElement(icon, { sx: { fontSize: 17 } })}
      <Typography variant="caption" sx={{ color: "inherit", whiteSpace: "nowrap" }}>
        {children}
      </Typography>
    </Stack>
  );
}

export default function ListingCard({ item }) {
  const [photos, setPhotos] = useState(null);

  useEffect(() => {
    let alive = true;
    loadPhotos(item.id, item.photoOrder).then((result) => {
      if (alive) setPhotos(result);
    });
    return () => {
      alive = false;
    };
  }, [item.id]);

  const cover = photos && photos.length > 0 ? photos[0] : null;
  const isFresh =
    (item.dateEdit || item.dateFrom || 0) > Date.now() - 7 * 86400000;

  return (
    <Card
      sx={{
        height: "100%",
        overflow: "hidden",
        transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: shadow.md,
          borderColor: tokens.blue100,
        },
        ...(HALLOWEEN ? CARD_SPOOK_HOVER : CHRISTMAS ? CARD_FROST_HOVER : {}),
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={"/kvartyra/" + encodeURIComponent(item.id)}
        sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
      >
        <Box
          sx={{
            position: "relative",
            aspectRatio: "4 / 3",
            backgroundColor: tokens.line2,
            overflow: "hidden",
          }}
        >
          {photos === null && (
            <Skeleton variant="rectangular" width="100%" height="100%" />
          )}

          {photos !== null && cover && (
            <PhotoImage
              src={cover.thumbUrl}
              fallbackSrc={cover.url}
              alt={listingTitle(item)}
              loading="lazy"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          )}

          {photos !== null && !cover && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ height: "100%", color: tokens.ink400, gap: 0.5 }}
            >
              <ImageNotSupportedOutlined sx={{ fontSize: 30 }} />
              <Typography variant="caption">Фото готуються</Typography>
            </Stack>
          )}

          {/* Before the badges, so the snow settles behind them, not over them. */}
          {CHRISTMAS && <CardFrost />}

          <Stack
            direction="row"
            spacing={0.75}
            sx={{ position: "absolute", top: 10, left: 10 }}
          >
            {isFresh && (
              <Chip
                size="small"
                label="Нове"
                sx={{
                  backgroundColor: tokens.green600,
                  color: "#fff",
                  fontWeight: 700,
                  boxShadow: shadow.sm,
                }}
              />
            )}
          </Stack>

          {photos && photos.length > 1 && (
            <Chip
              size="small"
              icon={<PhotoLibraryOutlined sx={{ fontSize: 15, color: "#fff !important" }} />}
              label={photos.length}
              sx={{
                position: "absolute",
                bottom: 10,
                right: 10,
                backgroundColor: "rgba(14,27,46,0.72)",
                color: "#fff",
                backdropFilter: "blur(4px)",
                "& .MuiChip-label": { pl: 0.5, pr: 1 },
              }}
            />
          )}

          {HALLOWEEN && <CardSpooks />}

          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              left: 10,
              px: 1.4,
              py: 0.6,
              borderRadius: radius.sm,
              backgroundColor: "rgba(255,255,255,0.94)",
              backdropFilter: "blur(6px)",
              boxShadow: shadow.sm,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {formatPrice(item.cost, item.currency)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2, display: "flex", flexDirection: "column", flexGrow: 1, gap: 1 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.35 }}>
            {listingTitle(item)}
          </Typography>

          <Stack direction="row" spacing={0.6} alignItems="center">
            <PlaceOutlined sx={{ fontSize: 17, color: tokens.blue600 }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {districtLabel(item.region) || "Львів"}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={1.6}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: "auto", pt: 1 }}
          >
            <Feature icon={<KingBedOutlined />}>{roomsLabel(item.rooms)}</Feature>
            <Feature icon={<StairsOutlined />}>{floorLabel(item.floor)}</Feature>
            {item.furniture ? (
              <Feature icon={<ChairOutlined />}>Меблі</Feature>
            ) : null}
            {item.technology ? (
              <Feature icon={<KitchenOutlined />}>Техніка</Feature>
            ) : null}
          </Stack>

          <Typography variant="caption" sx={{ color: tokens.ink400 }}>
            Оновлено {relativeDate(item.dateEdit || item.dateFrom)}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
}
